import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { DossierStatus, Tables } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { DOSSIER_JOURNEY_STEPS } from "@/lib/app/dossier-status";
import type { AdminSalarieWithRelations, SalarieFilters } from "./admin-salaries";
import type { DrhDossierPartage } from "@/lib/drh-partage";
import { fetchAllRows, type FetchAllResult } from "@/lib/data/fetch-all";

// =============================================================================
// Hooks data DRH — espace pilotage côté entreprise
//
// Le DRH voit les salariés et dossiers de son périmètre (société/filiale/BU/
// site/opération) via la table `user_scopes` et les fonctions SQL `has_access_*`.
// RLS impose déjà ce filtre côté serveur ; les requêtes ci-dessous renvoient
// donc directement le périmètre visible.
// =============================================================================

export type UserScope = Tables<"user_scopes">;

// -----------------------------------------------------------------------------
// Types enrichis
// -----------------------------------------------------------------------------
export type DrhDossier = Tables<"drh_dossiers"> & {
  salarie:
    | (Pick<
        Tables<"drh_salaries">,
        "id" | "matricule" | "civilite" | "prenom" | "nom" | "is_vip" | "salary_band"
      > & {
        societe: Pick<Tables<"societes">, "id" | "nom" | "slug"> | null;
        filiale: Pick<Tables<"filiales">, "id" | "nom"> | null;
        business_unit: Pick<Tables<"business_units">, "id" | "nom"> | null;
        site: Pick<Tables<"sites">, "id" | "nom" | "ville"> | null;
      })
    | null;
  operation: Pick<Tables<"operations">, "id" | "nom" | "slug" | "annee"> | null;
};

// Vue drh_dossiers : méta seulement (pas de consultant assigné, pas de
// contenu). Le salarié joint vient de la vue drh_salaries.
const DRH_DOSSIER_SELECT = `
  *,
  salarie:drh_salaries!dossiers_salarie_id_fkey (
    id, matricule, civilite, prenom, nom, is_vip, salary_band,
    societe:societes!salaries_societe_id_fkey ( id, nom, slug ),
    filiale:filiales!salaries_filiale_id_fkey ( id, nom ),
    business_unit:business_units!salaries_business_unit_id_fkey ( id, nom ),
    site:sites!salaries_site_id_fkey ( id, nom, ville )
  ),
  operation:operations!dossiers_operation_id_fkey (
    id, nom, slug, annee
  )
`;

// =============================================================================
// SCOPE — qui je suis et quel périmètre je vois
// =============================================================================

/**
 * Récupère les scopes du DRH connecté.
 * Sert à proposer un sélecteur de société/filiale si le DRH a plusieurs scopes.
 */
export function useMyScopes() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["drh", "my-scopes", user?.id ?? "anon"] as const,
    enabled: Boolean(user?.id),
    queryFn: async (): Promise<UserScope[]> => {
      if (!user) return [];
      const { data, error } = await supabase.from("user_scopes").select("*").eq("user_id", user.id);
      if (error) throw error;
      return (data ?? []) as UserScope[];
    },
  });
}

// =============================================================================
// STATISTIQUES — KPI dashboard DRH
// =============================================================================

export interface DrhStats {
  salaries: {
    total: number;
    avec_dossier: number;
  };
  dossiers: {
    total: number;
    actifs: number; // tout ce qui n'est pas closed
    en_completion: number;
    en_production: number;
    en_restitution: number;
    livres: number;
    /** Dossiers marqués « urgent » (B4). Seul le drapeau est exposé au DRH. */
    urgents: number;
    taux_completion_pct: number; // 0–100
  };
  restitutions: {
    a_venir_7j: number;
    a_venir_30j: number;
  };
}

const STATUSES_COMPLETION: DossierStatus[] = ["complete_incomplet", "complete_complet"];
const STATUSES_PRODUCTION: DossierStatus[] = ["en_production"];
const STATUSES_RESTITUTION: DossierStatus[] = [
  "a_restituer",
  "restitue_avec_rdv",
  "restitue_sans_rdv",
];
const STATUSES_LIVRES: DossierStatus[] = ["maj_sans_restitution", "maj_avec_restitution"];

/**
 * Stats agrégées du périmètre DRH visible (filtré par RLS).
 *
 * Implémentation : on fait plusieurs requêtes `count` ciblées, plus efficace
 * qu'un fetch global. RLS filtre déjà sur le scope du DRH.
 */
export function useDrhStats(args?: {
  /** Si fourni, restreint la stat à une société précise (parmi les scopes du DRH). */
  societeId?: string;
}) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["drh", "stats", user?.id ?? "anon", args?.societeId ?? "all"] as const,
    enabled: Boolean(user?.id),
    queryFn: async (): Promise<DrhStats> => {
      // ---- Salariés ------------------------------------------------------
      const salTotalQ = supabase.from("drh_salaries").select("id", { count: "exact", head: true });
      const salAvecDossierQ = supabase
        .from("drh_dossiers")
        .select("salarie_id", { count: "exact", head: true });
      if (args?.societeId) {
        salTotalQ.eq("societe_id", args.societeId);
      }

      const [salTotalRes, salAvecDossierRes] = await Promise.all([salTotalQ, salAvecDossierQ]);
      const salTotal = salTotalRes.count ?? 0;
      const salAvecDossier = salAvecDossierRes.count ?? 0;

      // ---- Dossiers par étape -------------------------------------------
      const baseDossierQ = () =>
        supabase.from("drh_dossiers").select("id", { count: "exact", head: true });

      const [totalRes, compRes, prodRes, restitRes, livresRes, urgentsRes] = await Promise.all([
        baseDossierQ(),
        baseDossierQ().in("status", STATUSES_COMPLETION),
        baseDossierQ().in("status", STATUSES_PRODUCTION),
        baseDossierQ().in("status", STATUSES_RESTITUTION),
        baseDossierQ().in("status", STATUSES_LIVRES),
        // Urgents encore en cours : les dossiers clos (restitués, MAJ, sans
        // suite) gardent le drapeau mais ne comptent plus.
        baseDossierQ()
          .eq("is_urgent", true)
          .not(
            "status",
            "in",
            "(restitue_avec_rdv,restitue_sans_rdv,maj_sans_restitution,maj_avec_restitution,sans_suite)",
          ),
      ]);

      const total = totalRes.count ?? 0;
      const enCompletion = compRes.count ?? 0;
      const enProduction = prodRes.count ?? 0;
      const enRestitution = restitRes.count ?? 0;
      const livres = livresRes.count ?? 0;

      // ---- Restitutions à venir (sur appointments) ----------------------
      const now = new Date();
      const in7 = new Date(now.getTime() + 7 * 24 * 3600 * 1000).toISOString();
      const in30 = new Date(now.getTime() + 30 * 24 * 3600 * 1000).toISOString();
      const nowIso = now.toISOString();

      const [rdv7Res, rdv30Res] = await Promise.all([
        supabase
          .from("drh_appointments")
          .select("id", { count: "exact", head: true })
          .gte("starts_at", nowIso)
          .lte("starts_at", in7)
          .in("status", ["planifie", "propose"]),
        supabase
          .from("drh_appointments")
          .select("id", { count: "exact", head: true })
          .gte("starts_at", nowIso)
          .lte("starts_at", in30)
          .in("status", ["planifie", "propose"]),
      ]);

      // Taux complétion : % de dossiers qui ont atteint au moins
      // "complete_complet" (ou plus loin dans le funnel).
      const completedOrBeyond = enCompletion + enProduction + enRestitution + livres;
      const tauxCompletion = total > 0 ? Math.round((completedOrBeyond / total) * 100) : 0;

      return {
        salaries: {
          total: salTotal,
          avec_dossier: salAvecDossier,
        },
        dossiers: {
          total,
          actifs: total - livres,
          en_completion: enCompletion,
          en_production: enProduction,
          en_restitution: enRestitution,
          livres,
          urgents: urgentsRes.count ?? 0,
          taux_completion_pct: tauxCompletion,
        },
        restitutions: {
          a_venir_7j: rdv7Res.count ?? 0,
          a_venir_30j: rdv30Res.count ?? 0,
        },
      };
    },
  });
}

// =============================================================================
// LISTE DOSSIERS DRH
// =============================================================================

export interface DrhDossierFilters {
  status?: DossierStatus | DossierStatus[];
  /** Filtre par étape macro (utilise DOSSIER_JOURNEY_STEPS). */
  journeyStep?: string;
  /** Recherche client-side dans nom/prénom/matricule. */
  search?: string;
  /** Restreint à une société. */
  societeId?: string;
  limit?: number;
}

export function useDrhDossiers(filters: DrhDossierFilters = {}) {
  return useQuery({
    queryKey: ["drh", "dossiers", filters] as const,
    queryFn: async (): Promise<FetchAllResult<DrhDossier>> => {
      const build = () => {
        let q = supabase
          .from("drh_dossiers")
          .select(DRH_DOSSIER_SELECT)
          .order("updated_at", { ascending: false });
        if (filters.status) {
          if (Array.isArray(filters.status)) {
            q = q.in("status", filters.status);
          } else {
            q = q.eq("status", filters.status);
          }
        } else if (filters.journeyStep) {
          const step = DOSSIER_JOURNEY_STEPS.find((s) => s.key === filters.journeyStep);
          if (step) {
            q = q.in("status", step.statuses as DossierStatus[]);
          }
        }
        return q;
      };
      // Limite explicite : requête unique ; sinon chargement complet par lots.
      const { rows, truncated } = filters.limit
        ? await (async () => {
            const { data, error } = await build().range(0, filters.limit! - 1);
            if (error) throw error;
            return { rows: (data ?? []) as DrhDossier[], truncated: false };
          })()
        : await fetchAllRows<DrhDossier>((from, to) => build().range(from, to));

      // Société : on filtre côté salarie.societe.id (RLS gère déjà l'accès)
      const filtered = filters.societeId
        ? rows.filter((r) => r.salarie?.societe?.id === filters.societeId)
        : rows;

      // Recherche client-side
      if (filters.search) {
        const term = filters.search.toLowerCase();
        return {
          rows: filtered.filter((r) => {
            const hay = [r.salarie?.prenom, r.salarie?.nom, r.salarie?.matricule]
              .filter(Boolean)
              .join(" ")
              .toLowerCase();
            return hay.includes(term);
          }),
          truncated,
        };
      }
      return { rows: filtered, truncated };
    },
  });
}

// =============================================================================
// RESTITUTIONS À VENIR (RDV sur ma population)
// =============================================================================

export type DrhUpcomingAppointment = Tables<"drh_appointments"> & {
  salarie: Pick<Tables<"drh_salaries">, "id" | "prenom" | "nom" | "matricule"> | null;
  consultant: Pick<Tables<"profiles">, "user_id" | "prenom" | "nom" | "email"> | null;
};

/** Retourne les prochains RDV du périmètre DRH (filtrés par RLS via dossier_id). */
export function useDrhUpcomingAppointments(limit = 10) {
  return useQuery({
    queryKey: ["drh", "upcoming-appointments", limit] as const,
    queryFn: async (): Promise<DrhUpcomingAppointment[]> => {
      const nowIso = new Date().toISOString();
      const { data, error } = await supabase
        .from("drh_appointments")
        .select(
          `
          id, dossier_id, salarie_id, consultant_id, starts_at, ends_at, status, channel,
          consultant:profiles!appointments_consultant_id_fkey (
            user_id, prenom, nom, email
          )
        `,
        )
        .gte("starts_at", nowIso)
        .in("status", ["planifie", "propose"])
        .order("starts_at", { ascending: true })
        .limit(limit);
      if (error) throw error;
      const appointments = (data ?? []) as unknown as Array<
        Tables<"drh_appointments"> & { consultant: DrhUpcomingAppointment["consultant"] }
      >;

      // Identité des salariés via la vue restreinte (deux requêtes : PostgREST
      // ne garantit pas l'embed rendez-vous -> vue).
      const salarieIds = Array.from(
        new Set(appointments.map((a) => a.salarie_id).filter((v): v is string => Boolean(v))),
      );
      const salarieById = new Map<string, DrhUpcomingAppointment["salarie"]>();
      if (salarieIds.length > 0) {
        const { data: sals, error: sErr } = await supabase
          .from("drh_salaries")
          .select("id, prenom, nom, matricule")
          .in("id", salarieIds);
        if (sErr) throw sErr;
        for (const s of sals ?? []) salarieById.set(s.id, s);
      }

      return appointments.map((a) => ({
        ...a,
        salarie: a.salarie_id ? (salarieById.get(a.salarie_id) ?? null) : null,
      }));
    },
  });
}

// =============================================================================
// DÉTAIL D'UN DOSSIER (DRH lecture seule)
// =============================================================================

export type DrhDossierDetail = Tables<"drh_dossiers"> & {
  salarie:
    | (Tables<"drh_salaries"> & {
        societe: Pick<Tables<"societes">, "id" | "nom" | "slug"> | null;
        filiale: Pick<Tables<"filiales">, "id" | "nom"> | null;
        business_unit: Pick<Tables<"business_units">, "id" | "nom"> | null;
        site: Pick<Tables<"sites">, "id" | "nom" | "ville"> | null;
      })
    | null;
  operation: Pick<Tables<"operations">, "id" | "nom" | "slug" | "annee"> | null;
};

const DRH_DOSSIER_DETAIL_SELECT = `
  *,
  salarie:drh_salaries!dossiers_salarie_id_fkey (
    *,
    societe:societes!salaries_societe_id_fkey ( id, nom, slug ),
    filiale:filiales!salaries_filiale_id_fkey ( id, nom ),
    business_unit:business_units!salaries_business_unit_id_fkey ( id, nom ),
    site:sites!salaries_site_id_fkey ( id, nom, ville )
  ),
  operation:operations!dossiers_operation_id_fkey ( id, nom, slug, annee )
`;

export function useDrhDossierDetail(dossierId: string | undefined) {
  return useQuery({
    queryKey: dossierId
      ? (["drh", "dossier-detail", dossierId] as const)
      : (["drh", "dossier-detail", "none"] as const),
    enabled: Boolean(dossierId),
    queryFn: async (): Promise<DrhDossierDetail | null> => {
      if (!dossierId) return null;
      const { data, error } = await supabase
        .from("drh_dossiers")
        .select(DRH_DOSSIER_DETAIL_SELECT)
        .eq("id", dossierId)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as DrhDossierDetail | null) ?? null;
    },
  });
}

// =============================================================================
// DOSSIER PARTAGÉ (RPC drh_dossier_partage, accord du salarié)
// =============================================================================

/** Contenu d'un dossier visible par le DRH selon l'accord de partage. */
export function useDrhDossierPartage(dossierId: string | undefined) {
  return useQuery({
    queryKey: dossierId
      ? (["drh", "dossier-partage", dossierId] as const)
      : (["drh", "dossier-partage", "none"] as const),
    enabled: Boolean(dossierId),
    queryFn: async (): Promise<DrhDossierPartage> => {
      if (!dossierId) throw new Error("dossierId manquant");
      const { data, error } = await supabase.rpc("drh_dossier_partage", {
        p_dossier_id: dossierId,
      });
      if (error) throw error;
      return data as unknown as DrhDossierPartage;
    },
  });
}

// =============================================================================
// CONTACTS AON DU DRH (chefs de projet de ses opérations)
// =============================================================================

export interface DrhAonContact {
  operation_id: string;
  operation_nom: string;
  societe_nom: string | null;
  chef_projet: Pick<
    Tables<"profiles">,
    "user_id" | "prenom" | "nom" | "email" | "avatar_url"
  > | null;
}

export function useDrhAonContacts() {
  return useQuery({
    queryKey: ["drh", "aon-contacts"] as const,
    queryFn: async (): Promise<DrhAonContact[]> => {
      const { data, error } = await supabase
        .from("operations")
        .select(
          `
          id, nom,
          societe:societes!operations_societe_id_fkey ( nom ),
          chef_projet:profiles!operations_chef_projet_fk (
            user_id, prenom, nom, email, avatar_url
          )
        `,
        )
        .not("chef_projet_user_id", "is", null)
        .order("nom", { ascending: true });
      if (error) throw error;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return ((data ?? []) as any[]).map((r) => ({
        operation_id: r.id,
        operation_nom: r.nom,
        societe_nom: r.societe?.nom ?? null,
        chef_projet: r.chef_projet ?? null,
      }));
    },
  });
}

// =============================================================================
// LISTE SALARIÉS DRH
// =============================================================================

export type DrhSalarie = Tables<"drh_salaries"> & {
  societe: Pick<Tables<"societes">, "id" | "nom" | "slug"> | null;
  filiale: Pick<Tables<"filiales">, "id" | "nom"> | null;
  business_unit: Pick<Tables<"business_units">, "id" | "nom"> | null;
  site: Pick<Tables<"sites">, "id" | "nom" | "ville"> | null;
};

export interface DrhSalarieFilters {
  search?: string;
  societeId?: string;
  isPresent?: boolean;
  limit?: number;
}

export function useDrhSalaries(filters: DrhSalarieFilters = {}) {
  return useQuery({
    queryKey: ["drh", "salaries", filters] as const,
    queryFn: async (): Promise<DrhSalarie[]> => {
      let q = supabase
        .from("drh_salaries")
        .select(
          `
          *,
          societe:societes!salaries_societe_id_fkey ( id, nom, slug ),
          filiale:filiales!salaries_filiale_id_fkey ( id, nom ),
          business_unit:business_units!salaries_business_unit_id_fkey ( id, nom ),
          site:sites!salaries_site_id_fkey ( id, nom, ville )
        `,
        )
        .order("nom", { ascending: true })
        .limit(filters.limit ?? 200);

      if (filters.societeId) q = q.eq("societe_id", filters.societeId);
      if (filters.isPresent !== undefined) q = q.eq("is_present", filters.isPresent);

      const { data, error } = await q;
      if (error) throw error;
      const rows = (data ?? []) as DrhSalarie[];

      if (filters.search) {
        const term = filters.search.toLowerCase();
        return rows.filter((r) => {
          const hay = [r.prenom, r.nom, r.matricule, r.email_pro]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return hay.includes(term);
        });
      }
      return rows;
    },
  });
}

// =============================================================================
// REPORTING DRH — agrégats anonymisés (k-anonymat) via RPC SQL
// =============================================================================

/** Un segment de ventilation (par BU / catégorie / département / région / opération). */
export interface DrhReportingSegment {
  key: string;
  label: string;
  /** null = masqué (effectif < k). */
  salaries: number | null;
  salaries_masked: boolean;
  actives: number | null;
  bilans: number | null;
  /** Salariés présents au sens D2 (compte non clos, date de sortie nulle ou future). */
  presents?: number | null;
  /** Salariés distincts inscrits à au moins une formation (masqué sous k). */
  formation_inscrits?: number | null;
  formation_inscrits_masked?: boolean;
  /** Salariés distincts présents à au moins une formation. */
  formation_presents?: number | null;
  /** Présents ayant refusé le contact (refus d'adhésion). */
  refus_adhesion?: number | null;
}

/** Une barre d'histogramme par âge. */
export interface DrhReportingHistBucket {
  age: number;
  /** null = masqué (effectif < k). */
  count: number | null;
  masked: boolean;
}

/** Une barre d'histogramme par année calendaire (ex : dates de TPN). */
export interface DrhReportingYearBucket {
  annee: number;
  /** null = masqué (effectif < k). */
  count: number | null;
  masked: boolean;
  /** 'YYYY' en annuel, 'YYYY-T1'..'YYYY-T4' en trimestriel (D4). */
  periode?: string;
  /** Trimestre 1..4 en mode trimestriel, null en annuel. */
  trimestre?: number | null;
}

/** Une session de formation avec présence agrégée. */
export interface DrhReportingFormation {
  id: string;
  titre: string;
  starts_at: string | null;
  ends_at: string | null;
  channel: string | null;
  lieu: string | null;
  /** null = masqué (effectif < k). */
  inscrits: number | null;
  inscrits_masked: boolean;
  presents: number | null;
}

/** Listes sélectionnables pour les filtres (indépendantes des filtres en cours). */
export interface DrhReportingFilterOptions {
  business_units: { id: string; nom: string }[];
  departements: string[];
  categories: string[];
  salary_bands: string[];
  operations: { id: string; nom: string }[];
  regions: string[];
}

/** Pas de regroupement des séries temporelles (D4). */
export type DrhReportingPas = "annee" | "trimestre";

/** Filtres applicables au reporting DRH. */
export interface DrhReportingFilters {
  societeId?: string;
  businessUnitId?: string;
  categorie?: string;
  salaryBand?: string;
  dept?: string;
  /** 'inscrit' | 'present' */
  formation?: "inscrit" | "present";
  status?: DossierStatus[];
  operationId?: string;
  region?: string;
  /** 'annee' | 'trimestre' (défaut 'annee'). */
  pas?: DrhReportingPas;
  /** null = les deux ; 'sans' = estimation de base ; 'avec' = dernière simulation de rachat. */
  rachat?: "sans" | "avec";
}

export interface DrhReportingOverview {
  k: number;
  cohort_too_small: boolean;
  dossiers_total: number;
  participation: {
    eligibles: number;
    actives: number;
    cgu: number;
    formes: number;
    sortants: number;
    /** Salariés ayant refusé d'être contactés (R2) — k-anonymisé. */
    refus_contact?: number | null;
    /** Alias métier de refus_contact (D4) — k-anonymisé. */
    refus_adhesion?: number | null;
  };
  /** statut dossier -> nombre. */
  funnel: Record<string, number>;
  retraite: {
    carriere_longue: number | null;
    carriere_longue_masked: boolean;
    n_rachat: number;
    cout_rachat_moyen: number | null;
    /** Volume total de trimestres rachetés (§13a). */
    trimestres_rachetes: number;
    /** Points Agirc-Arrco moyens récupérés/MAJ (§13b). null si non alimenté. */
    points_agirc_arrco_moyen: number | null;
    /** Somme des points Agirc-Arrco sur les derniers bilans. null si non alimenté. */
    points_agirc_arrco_total: number | null;
    /** Nombre de dossiers disposant d'une valeur de points Agirc-Arrco. */
    n_points: number;
    /** Trimestres manquants moyens. */
    trimestres_manquants_moyen: number | null;
    /** Trimestres validés moyens (derniers bilans). */
    trimestres_valides_moyen: number | null;
    /** Somme des trimestres validés (derniers bilans). */
    trimestres_valides_total: number | null;
    /** Nombre de dossiers disposant d'un comptage de trimestres validés. */
    n_trimestres_valides: number;
    hist_depart: DrhReportingHistBucket[];
    hist_taux_plein: DrhReportingHistBucket[];
    /** Distribution des dates de taux plein naturel (TPN) par année. */
    hist_tpn_annee: DrhReportingYearBucket[];
    /**
     * Retour Aon 06/08 (R1/R3) : premières dates de départ possibles par année,
     * sans rachat (estimation de base), avec rachat (dernière simulation de
     * rachat) et pour les carrières longues. Absents sur une RPC antérieure.
     */
    hist_premiere_date_annee?: DrhReportingYearBucket[];
    hist_premiere_date_annee_rachat?: DrhReportingYearBucket[];
    hist_premiere_date_annee_carriere_longue?: DrhReportingYearBucket[];
    /** Dossiers disposant d'une simulation de rachat réussie. */
    n_rachat_dossiers?: number;
  };
  /**
   * Retour Aon 06/08 (P10) : sorties (clôture du compte) — total, départs en
   * retraite (volontaires + mises à la retraite), répartition par motif et par
   * année. Effectifs k-anonymisés (null = masqué).
   */
  sorties?: {
    total: number;
    retraite_total: number | null;
    par_motif: { key: string; count: number | null; masked: boolean }[];
    par_annee: DrhReportingYearBucket[];
  };
  /**
   * Retour Aon 06/08 (R4) : droits récupérés consignés par les consultants —
   * volumes OBTENUS (trimestres rachetés / gagnés, points Agirc-Arrco) et en
   * cours de régularisation. Null = masqué (moins de k dossiers concernés).
   */
  droits_recuperes?: {
    n_dossiers: number | null;
    n_dossiers_masked: boolean;
    trimestres_rachetes: number | null;
    trimestres_gagnes: number | null;
    points_agirc_arrco: number | null;
    trimestres_en_cours: number | null;
    points_en_cours: number | null;
    /** P4 : somme des gains annuels estimés des lignes « obtenu ». */
    gain_annuel_total?: number | null;
    /** P4 : même calcul sur les lignes « identifie » et « demande ». */
    gain_annuel_en_cours?: number | null;
    /** P4 : ventilation par motif (key = motif ou « non_renseigne »). */
    par_motif?: {
      key: string;
      n_dossiers: number | null;
      masked: boolean;
      trimestres: number | null;
      points: number | null;
      gain_annuel: number | null;
    }[];
  };
  appetences: {
    plus_tot: number | null;
    plus_tard: number | null;
    rupture: number | null;
    retraite_progressive: number | null;
    rachat: number | null;
    cumul: number | null;
  };
  formation: {
    inscrits: number;
    presents: number;
  };
  /**
   * Évaluation de la prestation par les salariés (CDC §16). Effectifs et
   * moyenne k-anonymisés (null si moins de k évaluations).
   */
  satisfaction: {
    n_evaluations: number | null;
    n_evaluations_masked: boolean;
    note_moyenne: number | null;
    nps: number | null;
    n_reco: number;
    distribution: { note: number; count: number }[];
  };
  /**
   * Croisement formation × connexion × bilan (CDC §8b). Effectifs k-anonymisés
   * (null = masqué). « connecté » = auth.users.last_sign_in_at non nul (vraie
   * connexion, pas seulement compte créé).
   */
  formation_connexion?: {
    formes: number | null;
    formes_connectes: number | null;
    formes_connectes_bilan: number | null;
    formes_connectes_incomplets: number | null;
    formes_non_connectes: number | null;
    non_formes: number | null;
  };
  formations: DrhReportingFormation[];
  ventilation: {
    bu: DrhReportingSegment[];
    categorie: DrhReportingSegment[];
    salary_band: DrhReportingSegment[];
    departement: DrhReportingSegment[];
    region: DrhReportingSegment[];
    operation: DrhReportingSegment[];
  };
  filter_options: DrhReportingFilterOptions;
}

/**
 * Reporting agrégé du périmètre DRH.
 *
 * Délègue toute l'agrégation à la RPC SQL `drh_reporting_overview`
 * (SECURITY INVOKER → RLS applique le scope du DRH). Les cellules dont
 * l'effectif est < k sont masquées côté SQL (k-anonymat) ; le seuil k est
 * fixé côté serveur (réglage app_settings.drh_seuil_anonymat, défaut 5) et
 * renvoyé dans la réponse.
 */
export function useDrhReporting(args?: DrhReportingFilters) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["drh", "reporting", user?.id ?? "anon", args ?? {}] as const,
    enabled: Boolean(user?.id),
    queryFn: async (): Promise<DrhReportingOverview> => {
      const { data, error } = await supabase.rpc("drh_reporting_overview", {
        p_societe_id: args?.societeId ?? undefined,
        p_business_unit_id: args?.businessUnitId ?? undefined,
        p_categorie: args?.categorie ?? undefined,
        p_salary_band: args?.salaryBand ?? undefined,
        p_dept: args?.dept ?? undefined,
        p_formation: args?.formation ?? undefined,
        p_status: args?.status ?? undefined,
        p_operation_id: args?.operationId ?? undefined,
        p_region: args?.region ?? undefined,
        p_pas: args?.pas ?? "annee",
        p_rachat: args?.rachat ?? undefined,
      });
      if (error) throw error;
      return data as unknown as DrhReportingOverview;
    },
  });
}

// -----------------------------------------------------------------------------
// Évolution de la répartition des dossiers dans le temps (retour 31/07 — B10)
// -----------------------------------------------------------------------------
export interface DrhStatusHistoryPoint {
  date: string;
  total: number;
  /** Effectif par statut ; null = masqué (< k, seuil d'anonymat). */
  statuses: Record<string, number | null>;
}

export interface DrhStatusHistorySeries {
  k: number;
  points: DrhStatusHistoryPoint[];
}

/**
 * Série hebdomadaire de la répartition des dossiers par état, reconstituée
 * depuis l'historique des changements d'état (RPC drh_status_history_series,
 * SECURITY INVOKER → périmètre RLS du DRH, k-anonymat point par point).
 */
export function useDrhStatusHistorySeries(args?: { societeId?: string; weeks?: number }) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["drh", "status-history-series", user?.id ?? "anon", args ?? {}] as const,
    enabled: Boolean(user?.id),
    queryFn: async (): Promise<DrhStatusHistorySeries> => {
      const { data, error } = await supabase.rpc("drh_status_history_series", {
        p_societe_id: args?.societeId ?? undefined,
        p_weeks: args?.weeks ?? 26,
      });
      if (error) throw error;
      return (data ?? { k: 0, points: [] }) as unknown as DrhStatusHistorySeries;
    },
  });
}

// =============================================================================
// VUES DRH (accord de partage)
// =============================================================================
// Un DRH pur ne lit plus `salaries` ni `dossiers` : ces hooks interrogent les
// vues `drh_salaries` / `drh_dossiers` (identité + champs RH, méta du dossier).
// Elles servent aux composants mutualisés (liste, fiche) quand l'espace est
// DRH. Les données renvoyées ont la même forme que leurs équivalents admin.

const DRH_SALARIE_SELECT = `
  *,
  societe:societes!salaries_societe_id_fkey ( id, nom, slug ),
  operation:operations!salaries_operation_id_fkey ( id, nom, slug, annee )
`;

/** Liste des salariés du périmètre DRH via la vue drh_salaries. */
export function useDrhViewSalaries(filters: SalarieFilters = {}, enabled = true) {
  return useQuery({
    queryKey: ["drh", "view-salaries", filters] as const,
    enabled,
    queryFn: async (): Promise<FetchAllResult<AdminSalarieWithRelations>> => {
      const build = () => {
        let q = supabase
          .from("drh_salaries")
          .select(DRH_SALARIE_SELECT)
          .order("nom", { ascending: true, nullsFirst: false });
        if (filters.societeId) q = q.eq("societe_id", filters.societeId);
        if (filters.operationId) q = q.eq("operation_id", filters.operationId);
        if (typeof filters.isVip === "boolean") q = q.eq("is_vip", filters.isVip);
        if (typeof filters.isPresent === "boolean") {
          q = q.eq("is_present", filters.isPresent);
        }
        if (filters.search) {
          const term = filters.search.trim();
          if (term.length > 0) {
            q = q.or(
              [
                `nom.ilike.%${term}%`,
                `prenom.ilike.%${term}%`,
                `email_pro.ilike.%${term}%`,
                `matricule.ilike.%${term}%`,
              ].join(","),
            );
          }
        }
        return q;
      };
      // Limite explicite : requête unique ; sinon chargement complet par lots.
      if (filters.limit) {
        const { data, error } = await build().range(0, filters.limit - 1);
        if (error) throw error;
        return {
          rows: (data ?? []) as unknown as AdminSalarieWithRelations[],
          truncated: false,
        };
      }
      const res = await fetchAllRows<unknown>((from, to) => build().range(from, to));
      return {
        rows: res.rows as unknown as AdminSalarieWithRelations[],
        truncated: res.truncated,
      };
    },
  });
}

/** Fiche d'un salarié via la vue drh_salaries. */
export function useDrhViewSalarie(salarieId: string | undefined) {
  return useQuery({
    queryKey: ["drh", "view-salaries", "detail", salarieId ?? "none"] as const,
    queryFn: async (): Promise<AdminSalarieWithRelations | null> => {
      if (!salarieId) return null;
      const { data, error } = await supabase
        .from("drh_salaries")
        .select(DRH_SALARIE_SELECT)
        .eq("id", salarieId)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as AdminSalarieWithRelations | null;
    },
    enabled: Boolean(salarieId),
  });
}

/** Dernier dossier (méta) d'un salarié via la vue drh_dossiers. */
export function useDrhSalarieLatestDossier(salarieId: string | undefined) {
  return useQuery({
    queryKey: ["drh", "view-salaries", "detail", salarieId ?? "none", "dossier"] as const,
    queryFn: async (): Promise<Tables<"drh_dossiers"> | null> => {
      if (!salarieId) return null;
      const { data, error } = await supabase
        .from("drh_dossiers")
        .select("*")
        .eq("salarie_id", salarieId)
        .order("cycle_no", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: Boolean(salarieId),
  });
}

// =============================================================================
// SYNTHÈSE DE MISSION (D5a)
// =============================================================================
// Deux RPC security definer : les compteurs sont k-anonymisés (kc/km, seuil
// serveur), la liste nominative est restreinte aux salariés ayant autorisé le
// partage (date_only / full).

export interface DrhSyntheseCompteur {
  value: number | null;
  masked: boolean;
}

export interface DrhSyntheseMission {
  k: number;
  mission: {
    presents: number | null;
    presents_masked: boolean;
    sortis: number | null;
    sortis_masked: boolean;
    en_cours: number | null;
    en_cours_masked: boolean;
    prestations_realisees: number | null;
    prestations_realisees_masked: boolean;
    taux_realisation: number | null;
    anomalies_traitees: number | null;
    anomalies_traitees_masked: boolean;
    rachats_simules: number | null;
    rachats_simules_masked: boolean;
    refus_participation: number | null;
    refus_participation_masked: boolean;
  };
  departs_envisages: {
    au_plus_tot: { annee: number; count: number | null; masked: boolean }[];
    taux_plein: { annee: number; count: number | null; masked: boolean }[];
    annulation_decote: { annee: number; count: number | null; masked: boolean }[];
  };
}

export interface DrhSyntheseNominativeRow {
  salarie_id: string;
  dossier_id: string;
  nom: string | null;
  prenom: string | null;
  matricule: string | null;
  business_unit: string | null;
  operation: string | null;
  status: string;
  niveau: "date_only" | "full";
  date_taux_plein: string | null;
  age_taux_plein_annees: number | null;
  age_taux_plein_mois: number | null;
  /** Accord « full » uniquement. */
  premiere_date_depart?: string | null;
  age_premiere_date_annees?: number | null;
  pension_mensuelle_brute?: number | null;
}

export interface DrhSyntheseFilters {
  societeId?: string;
  businessUnitId?: string;
  operationId?: string;
  region?: string;
}

/** Agrégats de la synthèse de mission (k-anonymisés). */
export function useDrhSyntheseMission(args?: DrhSyntheseFilters, enabled = true) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["drh", "synthese-mission", user?.id ?? "anon", args ?? {}] as const,
    enabled: enabled && Boolean(user?.id),
    queryFn: async (): Promise<DrhSyntheseMission> => {
      const { data, error } = await supabase.rpc("drh_synthese_mission", {
        p_societe_id: args?.societeId ?? undefined,
        p_business_unit_id: args?.businessUnitId ?? undefined,
        p_operation_id: args?.operationId ?? undefined,
        p_region: args?.region ?? undefined,
      });
      if (error) throw error;
      return data as unknown as DrhSyntheseMission;
    },
  });
}

/** Détail nominatif des salariés ayant autorisé le partage. */
export function useDrhSyntheseNominative(args?: DrhSyntheseFilters, enabled = true) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["drh", "synthese-nominative", user?.id ?? "anon", args ?? {}] as const,
    enabled: enabled && Boolean(user?.id),
    queryFn: async (): Promise<DrhSyntheseNominativeRow[]> => {
      const { data, error } = await supabase.rpc("drh_synthese_nominative", {
        p_societe_id: args?.societeId ?? undefined,
        p_business_unit_id: args?.businessUnitId ?? undefined,
        p_operation_id: args?.operationId ?? undefined,
        p_region: args?.region ?? undefined,
      });
      if (error) throw error;
      return (data ?? []) as unknown as DrhSyntheseNominativeRow[];
    },
  });
}

export type DrhDossiersBySalarie = Map<string, Tables<"drh_dossiers">>;

/** Dernier dossier (méta) de chaque salarié d'une liste, en une requête. */
export function useDrhViewDossiersBySalaries(salarieIds: string[], enabled = true) {
  const sortedKey = [...salarieIds].sort().join(",");
  return useQuery({
    queryKey: ["drh", "view-salaries", "dossiers-map", sortedKey] as const,
    enabled: enabled && salarieIds.length > 0,
    queryFn: async (): Promise<DrhDossiersBySalarie> => {
      const { data, error } = await supabase
        .from("drh_dossiers")
        .select("*")
        .in("salarie_id", salarieIds)
        .order("cycle_no", { ascending: false });
      if (error) throw error;
      const map: DrhDossiersBySalarie = new Map();
      for (const d of (data ?? []) as Tables<"drh_dossiers">[]) {
        if (!map.has(d.salarie_id)) map.set(d.salarie_id, d);
      }
      return map;
    },
  });
}
